/**
 * @format
 */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import lodash from 'lodash';

const debug = require('debug')('advertsGatedSlice');

import {
  getGatedAdverts,
  Advertisement,
  CategoryList,
  FullCategory,
} from '../api/adserver';

import { RootState } from './rootReducer';
import { AppThunk } from './store';

interface AdvertListPayload {
  adverts: FullCategory | null;
}

type AdvertListsPayload = CategoryList;

type AdvertListState = {
  page: number;
  error: string | null;
  isLoading: boolean;
  categoryAdvertList: CategoryList;
  current: FullCategory | null;
};

let initialState: AdvertListState = {
  page: 0,
  isLoading: false,
  error: null,
  categoryAdvertList: {},
  current: null,
};

const getCategoriesDone = (state: AdvertListState) => {
  state.isLoading = false;
};

const advertsGatedSlice = createSlice({
  name: 'advertsGated',
  initialState,
  reducers: {
    displayCategoryAdvertList(state, action: PayloadAction<AdvertListPayload>) {
      const { adverts } = action.payload;
      state.current = adverts;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    getAdvertsListSuccess(state, action: PayloadAction<AdvertListsPayload>) {
      getCategoriesDone(state);
      state.categoryAdvertList = action.payload;
      state.error = null;
    },
    getAdvertsListFailed(state, action: PayloadAction<string>) {
      getCategoriesDone(state);
      state.categoryAdvertList = {};
      state.error = action.payload;
    },
    getAdvertsListStart(state) {
      state.isLoading = true;
    },
  },
});

export const {
  displayCategoryAdvertList,
  setCurrentPage,
  getAdvertsListSuccess,
  getAdvertsListFailed,
  getAdvertsListStart,
} = advertsGatedSlice.actions;

export const selectGatedCategoryAdvertsList = createSelector(
  (state: RootState) => state.advertsGated,
  (advertSlice: AdvertListState) => {
    //debug('selectCategoryAdvertsList categoryAdvertList are:');
    //debug(advertSlice.categoryAdvertList);
    const { isLoading, categoryAdvertList } = advertSlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => !cat.featured,
    );
    const filteredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c),
    );
    const advertsList = filteredList.reduce<Advertisement[]>((acc, cat) => {
      const categoryAdverts = lodash.flatMap(cat.adverts, (advert) => advert);
      return lodash.unionBy(acc, categoryAdverts, 'id');
    }, []);
    return {
      advertsList,
      categoryAdvertList: filteredList,
      isLoading,
    };
  },
);

export const selectGatedAdvertsList = createSelector(
  (state: RootState) => state.advertsGated,
  (advertSlice: AdvertListState) => {
    //debug('categoryAdvertList are:');
    //debug(advertSlice.categoryAdvertList);
    const { isLoading, categoryAdvertList } = advertSlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => !cat.featured,
    );
    const filteredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c),
    );
    const advertsList = filteredList.reduce<Advertisement[]>((acc, cat) => {
      const categoryAdverts = lodash.flatMap(cat.adverts, (advert) => advert);
      return lodash.unionBy(acc, categoryAdverts, 'id');
    }, []);
    return {
      advertsList,
      isLoading,
    };
  },
);

export const selectGatedFeaturedList = createSelector(
  (state: RootState) => state.advertsGated,
  (advertSlice: AdvertListState) => {
    //debug('selectFeaturedList - categoryAdvertList are:');
    //debug(advertSlice.categoryAdvertList);
    const { isLoading, categoryAdvertList } = advertSlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => cat.featured,
    );
    const featuredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c.adverts),
    );
    return {
      featuredList: featuredList,
      isLoading,
    };
  },
);

export const fetchGatedAdvertsList =
  (building: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getAdvertsListStart());
      const categories = await getGatedAdverts(building);
      dispatch(getAdvertsListSuccess(categories));
    } catch (err: unknown) {
      let message = 'unknown error';
      if (err instanceof Error) {
        message = err.toString();
      }
      dispatch(getAdvertsListFailed(message));
    }
  };

export default advertsGatedSlice.reducer;
